// Mapas base do back office: o mapa por tema oferece os três para escolha, e o
// mini-mapa da gaveta usa o padrão. Quem monta a camada é `mapaLeaflet.ts`.
//
// Três serviços do `server.arcgisonline.com`, e nenhum exige chave: o back
// office precisa desenhar num clone frio, sem ninguém ter pedido credencial a
// lugar nenhum. A CARTO ficou de fora por isso — ela **passou a exigir chave** e
// falha de um jeito que nenhuma verificação técnica pega: devolve HTTP 200,
// `image/png` válido, com "API KEY REQUIRED" carimbado dentro da imagem. Só
// olhando se vê.
//
// **Um provedor só, de propósito.** O consumidor que define o sucesso deste
// produto é o analista de ArcGIS (SPEC §4), e o mapa base que ele reconhece é o
// da Esri — comparar a geometria curada contra o mesmo fundo que ele vê no Pro
// tira uma variável da conversa. O custo está registrado abaixo.
//
// A lista é FIXA no código, e não configurável por ambiente. A atribuição de
// cada serviço viaja junto da URL na mesma constante, que é o ponto: tile server
// externo vem com termo de uso, e separar a URL do crédito é como se publica
// mapa sem a atribuição que a licença exige.
//
// **Os três são da Esri, e os termos são os dela — não ODbL.** "Não exige chave"
// é fato técnico (a URL responde sem credencial), NÃO leitura dos termos de uso:
// ninguém conferiu ainda se o uso por este back office cabe neles. Mapa base
// entregue a cliente, ou consumo pesado, pede ADR. Não há nenhum provedor de
// licença aberta na lista, então se a Esri bloquear ou mudar de termo, o mapa
// base fica cinza — não há para onde cair.
//
// **Por que saímos do OpenStreetMap:** os tiles do `tile.openstreetmap.org`
// voltavam **403** (relatado em 2026-09-28). Causa provável, NÃO medida: o
// nginx da interface manda `Referrer-Policy: no-referrer`
// (`apps/interface/seguranca.conf`), o pedido de tile sai sem `Referer`, e a
// política de uso do OSM recusa pedido de tile sem ele. Ver
// `decisions/correcoes/003-basemaps/`.

/** Um mapa base: de onde vêm os tiles, e sob que crédito. */
export interface Basemap {
  id: string;
  /** Rótulo no seletor. Curto: a legenda tem 282px. */
  nome: string;
  url: string;
  /** Exigida pelos termos da Esri. Vai na legenda e no canto do mapa. */
  atribuicao: string;
  /**
   * Último nível em que o serviço TEM tile próprio.
   *
   * Não é o zoom máximo do mapa: o Leaflet amplia o tile do último nível
   * disponível em vez de parar de desenhar. Sem isto, um serviço que parasse
   * antes apagaria o mapa base ao ser escolhido com o mapa fundo no zoom — e
   * some sem erro no console, o que se lê como "quebrou", não "acabou o
   * detalhe".
   *
   * Os 19 de hoje NÃO foram medidos contra os serviços. E em área sem dado o
   * serviço da Esri pode devolver imagem de "sem dado" com HTTP 200 — isso
   * `maxNativeZoom` não resolve, porque para o Leaflet é um tile válido.
   */
  zoomNativoMaximo: number;
  /**
   * Se o filtro de tema escuro se aplica a este serviço.
   *
   * O filtro é uma inversão de cor — escurece mapa de ruas muito bem, e
   * transforma imagem aérea num negativo onde vegetação fica rosa e água
   * branca. Inverter satélite não é "escuro", é ilegível.
   */
  inverteNoEscuro: boolean;
}

/**
 * Prefixo comum dos serviços de tile da Esri.
 *
 * A ordem dos eixos é **`{z}/{y}/{x}`**, e não o `{z}/{x}/{y}` do OSM. Trocada,
 * o mapa desenha tiles de outro lugar do mundo sem erro nenhum no console — o
 * defeito parece "o mapa está errado", não "a URL está errada".
 */
const ESRI = (servico: string): string =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/${servico}/MapServer/tile/{z}/{y}/{x}`;

export const BASEMAPS: readonly Basemap[] = [
  {
    id: "ruas",
    nome: "Ruas",
    url: ESRI("World_Street_Map"),
    atribuicao:
      "Esri, HERE, Garmin, USGS, Intermap, INCREMENT P, NRCan, METI, NGCC, " +
      "© colaboradores do OpenStreetMap e a comunidade GIS",
    zoomNativoMaximo: 19,
    inverteNoEscuro: true,
  },
  {
    id: "satelite",
    nome: "Satélite",
    url: ESRI("World_Imagery"),
    atribuicao: "Esri, Maxar, Earthstar Geographics e a comunidade GIS",
    zoomNativoMaximo: 19,
    inverteNoEscuro: false,
  },
  {
    id: "topografico",
    nome: "Topográfico",
    url: ESRI("World_Topo_Map"),
    atribuicao:
      "Esri, HERE, Garmin, Intermap, INCREMENT P, GEBCO, USGS, FAO, NPS, NRCAN, " +
      "GeoBase, IGN, METI, © colaboradores do OpenStreetMap e a comunidade GIS",
    zoomNativoMaximo: 19,
    inverteNoEscuro: true,
  },
] as const;

/** Ruas por padrão: é o mapa base em que o curador reconhece onde está. */
export const BASEMAP_PADRAO = "ruas";

const CHAVE = "atlas.basemap";

/**
 * Acha o mapa base pelo id, com o padrão como rede.
 *
 * Nunca devolve `undefined`: id desconhecido (chave antiga no armazenamento,
 * serviço removido daqui) cai no padrão. A alternativa é a tela quebrar por
 * causa de um valor que o próprio navegador guardou.
 */
export function acharBasemap(id: string | null | undefined): Basemap {
  return BASEMAPS.find((b) => b.id === id) ?? BASEMAPS.find((b) => b.id === BASEMAP_PADRAO)!;
}

/**
 * Lê a escolha guardada.
 *
 * `localStorage` dentro de `try`: em navegador com armazenamento bloqueado o
 * acesso LANÇA, e uma preferência de mapa base não pode derrubar a tela.
 */
export function lerBasemapSalvo(): string {
  try {
    return localStorage.getItem(CHAVE) ?? BASEMAP_PADRAO;
  } catch {
    return BASEMAP_PADRAO;
  }
}

/** Guarda a escolha. Falha em silêncio pela mesma razão que a leitura. */
export function salvarBasemap(id: string): void {
  try {
    localStorage.setItem(CHAVE, id);
  } catch {
    // Sem armazenamento, a escolha vale pela sessão. É o suficiente.
  }
}
